import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// ToolingShell — Photoshop-style workspace primitive.
//
// Desktop: middle canvas · right collapsible inspector · a DETACHED floating
// tool rail docked to the far right edge of the viewport (Lisa, 2026-07-19:
// the old left tool-strip sat right next to the app's own left nav Sidebar —
// two vertical bars side by side reading as one confusing double-rail.
// Moved to the opposite edge and taken fully out of the flex flow via
// `fixed`, so it reads as its own object: rounded plate, gap from the
// viewport edge, quiet shadow, no glow. It floats ABOVE the inspector when
// one is open rather than squeezing beside it — the same always-on-top
// convention HelpFab already uses at bottom-right. Vertically centered so
// it stays clear of HelpFab (bottom-right) and, on the page that also
// renders a bottom activation bar, clear of that too.
//
// Mobile (< md): unchanged — the tool-strip stays the horizontal scrolling
// row at the top of the stack. A fixed edge rail has nowhere safe to float
// on a narrow screen without sitting on top of content, so mobile keeps the
// original in-flow arrangement.
//
// ToolingShell has exactly one consumer today (TournamentSetup) — this is a
// direct layout change rather than an opt-in prop for that reason. Gate it
// behind a prop if a second consumer ever wants the old side-by-side strip.
// ─────────────────────────────────────────────────────────────────────────────

export type Tool = {
  id: string;
  label: string;
  icon: LucideIcon;
  done?: boolean;
  disabled?: boolean;
};

function ToolButton({ tool, isActive, onClick }: { tool: Tool; isActive: boolean; onClick: () => void }) {
  const Icon = tool.icon;
  return (
    <button
      type="button"
      disabled={tool.disabled}
      onClick={onClick}
      title={tool.label}
      className={cn(
        'group relative flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-md transition-colors',
        isActive
          ? 'bg-white/[0.08] text-ink-primary'
          : 'text-ink-secondary hover:bg-white/[0.05] hover:text-ink-primary',
        tool.disabled && 'opacity-40',
      )}
    >
      <Icon className={cn('h-[18px] w-[18px]', isActive && 'text-accent-500')} strokeWidth={1.6} />
      {tool.done && !isActive && (
        <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
      )}
    </button>
  );
}

export function ToolingShell({
  tools,
  activeId,
  onActiveChange,
  children,
  inspector,
  inspectorTitle,
}: {
  tools: Tool[];
  activeId: string;
  onActiveChange: (id: string) => void;
  children: ReactNode;
  inspector?: ReactNode;
  inspectorTitle?: string;
}) {
  const [inspectorOpen, setInspectorOpen] = useState(false);

  // Publish the attached rail's width while this shell is mounted, so other
  // viewport-anchored chrome (the global PageActionBar) can clear it — same
  // CSS-var idiom as Sidebar's --app-sidebar-w. Removed on unmount: pages
  // without the shell get no phantom offset.
  useEffect(() => {
    document.documentElement.style.setProperty('--tool-rail-w', '64px');
    return () => { document.documentElement.style.removeProperty('--tool-rail-w'); };
  }, []);

  return (
    <div className="flex min-h-[calc(100vh-200px)] flex-col overflow-hidden rounded-xl bg-paper-elevated md:flex-row">
      {/* Tool-strip — mobile only now: horizontal scroll row at the top of
          the stack. Desktop gets the detached floating rail below instead. */}
      <nav
        className="flex w-full shrink-0 gap-1 overflow-x-auto bg-paper-panel p-2 md:hidden"
        aria-label="Workspace tools"
      >
        {tools.map(tool => (
          <ToolButton key={tool.id} tool={tool} isActive={tool.id === activeId} onClick={() => onActiveChange(tool.id)} />
        ))}
      </nav>

      {/* Attached tool rail — desktop only. Placement v2 (Lisa, 2026-07-19,
          superseding the floating right-edge plate): a full-height vertical
          bar ATTACHED to the app navigation sidebar — sitting between the
          nav and the main page area, tracking the sidebar's collapse state
          via the --app-sidebar-w variable Sidebar.tsx publishes. PORTALED to
          document.body: rendered in place, `fixed` was being captured by the
          page's animated (transformed) ancestors — CSS makes `fixed` resolve
          against the nearest transformed box, so the rail drifted onto the
          page content instead of the viewport lane reserved for it (Lisa's
          screenshot, 2026-07-19 evening). The portal restores true viewport
          anchoring. Pages hosting this shell clear the strip with md:ml. */}
      {createPortal(
        <nav
          className="fixed bottom-0 top-0 z-30 hidden w-[64px] flex-col justify-center gap-1 bg-paper-panel p-2 md:flex"
          style={{ left: 'var(--app-sidebar-w, 240px)' }}
          aria-label="Workspace tools"
        >
          {tools.map(tool => (
            <ToolButton key={tool.id} tool={tool} isActive={tool.id === activeId} onClick={() => onActiveChange(tool.id)} />
          ))}
        </nav>,
        document.body,
      )}

      {/* Middle canvas */}
      <main className="flex-1 overflow-y-auto p-4 md:p-6">{children}</main>

      {/* Right inspector — full-width below on mobile, side panel on desktop */}
      {inspector && (
        <aside
          className={cn(
            'w-full shrink-0 overflow-hidden bg-paper-panel md:transition-[width] md:duration-300',
            inspectorOpen ? 'md:w-[280px]' : 'md:w-[36px]',
          )}
        >
          <div className="flex items-center justify-between px-3 py-2">
            <button
              type="button"
              onClick={() => setInspectorOpen(o => !o)}
              className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-ink-meta transition-colors hover:text-ink-primary"
              aria-label={inspectorOpen ? 'Collapse inspector' : 'Expand inspector'}
              aria-expanded={inspectorOpen}
            >
              <span className="text-ink-meta">{inspectorOpen ? '›' : '‹'}</span>
              {inspectorOpen && <span>{inspectorTitle ?? 'Inspector'}</span>}
            </button>
          </div>
          {inspectorOpen && (
            <div className="max-h-[50vh] overflow-y-auto p-4 md:h-[calc(100%-37px)] md:max-h-none">{inspector}</div>
          )}
        </aside>
      )}
    </div>
  );
}
